"use client";

import { useSyncExternalStore } from "react";

/**
 * Tiny cross-component flag: "is the locale-suggestion strip on screen?"
 *
 * `LocaleSuggestion` writes it; `CookieConsent` reads it and holds itself back
 * while the strip is up. The ORDER is deliberate and legally load-bearing: a
 * consent notice has to be in a language the visitor understands to be valid
 * consent (GDPR / PDPD), so we let them pick their language FIRST and the
 * cookie prompt then renders in that language.
 *
 * A module-level store (the `use-now.ts` pattern) rather than a context so no
 * provider has to wrap the layout — the two components are plain siblings.
 * `getServerSnapshot` returns false, which is also what SSR renders for both
 * banners (each is gated on a mounted/hydrated flag), so there's no mismatch.
 */
/**
 * ⛔ REFCOUNTED, because this is no longer a single-instance component. It was
 * a plain boolean written as `setLocaleSuggestionActive(target !== null)` by
 * every mounted copy — so on an app-frame route, where a fixed copy and an
 * inline copy both mount, the one that was NOT showing wrote `false` last and
 * clobbered the one that was. `CookieConsent` then failed to stand down and
 * both prompts rendered at once, which breaks the ordering the doc comment
 * above describes as a consent-validity requirement (review finding,
 * 2026-09-05). Only a copy that is actually on screen acquires.
 */
let count = 0;
const listeners = new Set<() => void>();

export function acquireLocaleSuggestion(): void {
  count += 1;
  if (count === 1) listeners.forEach((l) => l());
}

export function releaseLocaleSuggestion(): void {
  if (count === 0) return; // defensive — no-op if already cleared
  count -= 1;
  if (count === 0) listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => count > 0;
const getServerSnapshot = () => false;

export function useLocaleSuggestionActive(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
